import {FC, useState, useEffect, useRef} from 'react'
import classNames from 'classnames'
import styled, {keyframes} from 'styled-components'

import {CopyIcon, CheckIcon, RenderIf} from 'components'
import {colorCodes} from 'definitions/constant'

export interface CopyButtonWithTextProps {
  copyText?: string
  copyButtonText?: string
  copyTimeout?: number
  isLoading?: boolean
  onCopy?: () => void
  disabled?: boolean
  dataCy?: string
  className?: string
}

const shrink = keyframes`
  0% { stroke-width: 1.2; stroke: white; }
  100% { stroke-width: 1; stroke: ${colorCodes.gray1000}; }
`
const moveDown = keyframes`
  0% { transform: translate(0,0); }
  100% { transform: translate(1.2px,1.2px); }
`
const grow = keyframes`
  0% { stroke-width: 1; stroke: ${colorCodes.gray1000}; }
  100% { stroke-width: 1.2; stroke: white; }
`
const moveUp = keyframes`
  0% { transform: translate(1.2px,1.2px); }
  100% { transform: translate(0,0); }
`

const StyledCopyButton = styled.button`
  .copy-icon-svg path:first-child {
    animation: ${grow} 0.3s forwards;
  }
  .copy-icon-svg path:last-child {
    animation: ${moveUp} 0.3s forwards;
  }

  &:hover:not(.is-copied):not(:disabled) .copy-icon-svg path:first-child {
    animation: ${shrink} 0.15s forwards;
  }
  &:hover:not(.is-copied):not(:disabled) .copy-icon-svg path:last-child {
    animation: ${moveDown} 0.15s forwards;
  }
`

export const CopyButtonWithText: FC<CopyButtonWithTextProps> = ({
  copyText,
  copyButtonText = 'Copy',
  copyTimeout = 2000,
  onCopy,
  disabled,
  isLoading = false,
  dataCy = 'copy-button-with-text',
  className,
}) => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  const handleCopy = async () => {
    if (!copyText || disabled || copied) return
    try {
      await navigator.clipboard.writeText(copyText)
      onCopy?.()
      setCopied(true)
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(
        () => setCopied(false),
        copyTimeout,
      )
    } catch (error) {
      console.error('Failed to copy text to clipboard!', error)
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    }
  }, [])

  return (
    <StyledCopyButton
      onClick={handleCopy}
      disabled={!copyText || disabled}
      data-cy={dataCy}
      aria-label={copied ? 'Copied' : 'Copy to clipboard'}
      className={classNames(
        'relative flex items-center py-1.5 px-2 rounded-md border border-white/60 bg-transparent',
        'hover:bg-white/10 active:bg-white/15 transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-0',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        copied && 'is-copied',
        className,
      )}
    >
      <span className="text-xs w-16 h-4 karla text-white">
        {copied ? 'Copied!' : isLoading ? 'Loading...' : copyButtonText}
      </span>

      <RenderIf isTrue={copied}>
        <span
          className="check-mark-wrapper flex items-center justify-center animate-copy-check-draw"
          style={{strokeDasharray: 100, strokeDashoffset: 100}}
          data-cy={`${dataCy}-check-mark`}
        >
          <CheckIcon classes="w-5 h-5" strokeColor="text-white" />
        </span>
      </RenderIf>
      <RenderIf isTrue={!copied}>
        <span className="flex items-center">
          <CopyIcon width={20} height={20} datacy={`${dataCy}-copy-icon`} />
        </span>
      </RenderIf>
    </StyledCopyButton>
  )
}
